import axios from 'axios';
import * as STATUS from './chatStatus';
import { contractorAuth } from './contractorAuth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Talking to the chat endpoints.
 *
 * The one thing worth knowing from here: TALKING ABOUT A TICKET IS NOT A CHAT
 * ENDPOINT. Gordon's call was that chat and ticket comments are one thread
 * seen two ways, so `ticketMessages` and `sayOnTicket` below post to
 * /tickets/:id/comments - the endpoint the ticket page has always used, which
 * already admits staff and the assigned contractor and whose rows the
 * approver's review reads. There is no second table and no second thread.
 *
 * `authFor` returns undefined for a member of staff, so axios falls through to
 * the interceptor in lib/apiClient and picks up the Entra token; a contractor
 * gets their portal token. One expression for both kinds of caller, which is
 * the thing four pages in this project got wrong by trusting the interceptor
 * alone and answering 401 for every contractor.
 */
const authFor = () => contractorAuth();

export const people = () => axios
  .get(`${API_BASE}/chat/people`, authFor()).then((r) => r.data);

export const ticketsWith = (kind, id) => axios
  .get(`${API_BASE}/chat/with/${kind}/${id}/tickets`, authFor()).then((r) => r.data);

/*
 * WHICH of the threads with somebody: `thread` is a topic's id (below), and
 * nothing is General - what every call here meant before topics existed and
 * what an older server, which ignores the parameter, still answers.
 */
const inThread = (thread) => (thread ? { thread } : {});

/*
 * A PAGE AT A TIME (wave 3). A conversation opens on its newest 50 lines;
 * `page` asks for more: { before: seq } the lines above the oldest shown,
 * { through: messageId } everything from that message down to the newest
 * (a search result far back), { limit } how many. The answer says
 * `has_older` and `oldest_seq`. A server from before paging ignores all of
 * it and sends the whole thread, with no has_older - read as "nothing more".
 */
const paging = (page) => {
  const p = page || {};
  return {
    ...(p.before ? { before: p.before } : {}),
    ...(p.through ? { through: p.through } : {}),
    ...(p.limit ? { limit: p.limit } : {})
  };
};

export const directMessages = (kind, id, since, thread = null, page = null) => axios
  .get(`${API_BASE}/chat/with/${kind}/${id}/messages`, {
    ...authFor(), params: { ...(since ? { since } : {}), ...inThread(thread), ...paging(page) }
  }).then((r) => r.data);

/*
 * `mentions` (084) are the keys of the people the words @mention - sent only
 * when there are some, so a server from before mentions is posted exactly
 * what it always was.
 */
const withMentions = (mentions) => (Array.isArray(mentions) && mentions.length ? { mentions } : {});

export const sayDirect = (kind, id, body, thread = null, mentions = []) => axios
  .post(`${API_BASE}/chat/with/${kind}/${id}/messages`, { body, ...withMentions(mentions) },
    { ...authFor(), params: inThread(thread) })
  .then((r) => r.data);

/*
 * TOPICS - several named conversations with one person, under General.
 *
 * Gordon, 21 October: "allow new conversations to be created under General.
 * Also allow chats to be archived." A topic is another thread between the
 * same two people with a name on it; General is the one with no name
 * (`topic: null`). The list answers every thread with them, the archived
 * ones last and flagged, so the screen can fold those away rather than lose
 * them. Making one answers 409 when the two of you already have that name,
 * and 503 on a server that has not had migration 061.
 */
export const topicsWith = (kind, id) => axios
  .get(`${API_BASE}/chat/with/${kind}/${id}/topics`, authFor()).then((r) => r.data);

export const createTopic = (kind, id, topic) => axios
  .post(`${API_BASE}/chat/with/${kind}/${id}/topics`, { topic }, authFor())
  .then((r) => r.data);

/** Rename a topic, or archive it ({ archived: true }) and bring it back. */
export const updateThread = (threadId, changes) => axios
  .patch(`${API_BASE}/chat/threads/${threadId}`, changes, authFor())
  .then((r) => r.data);

/** The ticket's own comment thread. Not a chat table — see the note above. */
export const ticketMessages = (ticketId, since) => axios
  .get(`${API_BASE}/tickets/${ticketId}/comments`, {
    ...authFor(), params: since ? { since } : {}
  }).then((r) => r.data);

export const sayOnTicket = (ticketId, body) => axios
  .post(`${API_BASE}/tickets/${ticketId}/comments`, { body }, authFor())
  .then((r) => r.data);

/*
 * GROUPS - a conversation with more than two people in it, Slack's channels.
 *
 * Unlike talking about a ticket, this IS a chat table of its own, and that is
 * the right call rather than an inconsistency: a group belongs to no ticket,
 * so there is no ticket thread for it to be. What is said about a job still
 * goes on the job.
 *
 * Members are named by the same keys the people list uses - 'u:<uuid>' for
 * staff, 'c:<uuid>' for a contractor - so the picker can hand over exactly
 * what it was given without translating anything. And the people you may add
 * are exactly the people in your own list from `people()`: the server refuses
 * anyone else, which is what stops a group being a way round the directory
 * rules in Settings.
 */

/** Your groups, newest activity first, and whether you may start one. */
export const groups = () => axios
  .get(`${API_BASE}/chat/groups`, authFor()).then((r) => r.data);

/*
 * Since 079 a group can also be made BY RULE - `rules` is any of 'staff',
 * 'contractors', 'everyone' and 'department:<name>', and `exclude` leaves
 * named people out of them. Sent only when there are some, so a page on a
 * server before 079 posts exactly what it always did.
 */
export const createGroup = ({ name, topic, members, rules, exclude }) => axios
  .post(`${API_BASE}/chat/groups`, {
    name, topic, members,
    ...(rules && rules.length ? { rules, exclude: exclude || [] } : {})
  }, authFor())
  .then((r) => r.data);

/** The membership rules I may use when starting a group, with how many each covers. */
export const groupRules = () => axios
  .get(`${API_BASE}/chat/group-rules`, authFor()).then((r) => r.data);

/** One group: its name, topic, members and what you may do to it. */
export const group = (id) => axios
  .get(`${API_BASE}/chat/groups/${id}`, authFor()).then((r) => r.data);

export const updateGroup = (id, changes) => axios
  .patch(`${API_BASE}/chat/groups/${id}`, changes, authFor()).then((r) => r.data);

export const addMembers = (id, members) => axios
  .post(`${API_BASE}/chat/groups/${id}/members`, { members }, authFor())
  .then((r) => r.data);

/** Removing somebody else, or - with your own key - leaving. */
export const removeMember = (id, key) => axios
  .delete(`${API_BASE}/chat/groups/${id}/members/${encodeURIComponent(key)}`, authFor())
  .then((r) => r.data);

/*
 * TOPICS IN A GROUP (079) - "Boxes" and "Keys" beside the group's General,
 * as a person's topics sit beside theirs. Named on the messages as ?topic=,
 * and General when nothing is named, so these read what they always did
 * unless asked otherwise.
 */
const inTopic = (topic) => (topic ? { topic } : {});

export const groupMessages = (id, since, topic = null, page = null) => axios
  .get(`${API_BASE}/chat/groups/${id}/messages`, {
    ...authFor(), params: { ...(since ? { since } : {}), ...inTopic(topic), ...paging(page) }
  }).then((r) => r.data);

export const sayInGroup = (id, body, topic = null, mentions = []) => axios
  .post(`${API_BASE}/chat/groups/${id}/messages`, { body, ...withMentions(mentions) },
    { ...authFor(), params: inTopic(topic) })
  .then((r) => r.data);

/** General (id null), then the topics, archived last - each with its unread. */
export const groupTopics = (id) => axios
  .get(`${API_BASE}/chat/groups/${id}/topics`, authFor()).then((r) => r.data);

export const createGroupTopic = (id, topic) => axios
  .post(`${API_BASE}/chat/groups/${id}/topics`, { topic }, authFor()).then((r) => r.data);

/** Rename ({ topic }), archive ({ archived: true }) or bring back a group's topic. */
export const updateGroupTopic = (id, topicId, changes) => axios
  .patch(`${API_BASE}/chat/groups/${id}/topics/${topicId}`, changes, authFor()).then((r) => r.data);

/*
 * SEARCH (079) across every chat I can open: { q, from, in, offset, limit,
 * sort }. `in` is 'person:<key>', 'group:<id>' or 'ticket:<id>'. Answers
 * { results, next_offset }, each result with a plain-text snippet and the
 * ranges in it to highlight - never markup.
 */
export const searchChat = (params) => axios
  .get(`${API_BASE}/chat/search`, { ...authFor(), params }).then((r) => r.data);

/*
 * FILES - Gordon: "if they are related to the ticket it should go to the
 * ticket otherwise it should remain under general."
 *
 * So there are two places a file can go, and which one is decided by the
 * conversation it is dropped into, not by anything the person picks:
 *
 *  - On the General thread or in a group it stays in the conversation, saved
 *    with the message it came with (POST .../files). The server answers with
 *    the message row, attachments and all, in the same shape a re-read gives.
 *  - On a ticket it goes to the TICKET'S OWN FILES - the same endpoint the
 *    ticket page's Attach button uses - for the same reason a comment does:
 *    whoever picks the job up next looks on the ticket, not in somebody's
 *    chat. See `uploadToTicket`.
 *
 * Multipart, with the file under `file` and any words under `body`. No
 * Content-Type is set by hand: the browser has to write the boundary into it,
 * and a hand-written "multipart/form-data" without one is a body the server
 * cannot split.
 */
const upload = (url, file, body, params = {}, mentions = []) => {
  const form = new FormData();
  form.append('file', file);
  if (body) form.append('body', body);
  if (Array.isArray(mentions) && mentions.length) form.append('mentions', JSON.stringify(mentions));
  return axios.post(url, form, { ...authFor(), params }).then((r) => r.data);
};

// Into a topic when one is open (the query string, as for a message), and
// otherwise into General as before. `branch` (084) sends it as a reply in the
// branch off that message instead, and `also` posts it to the conversation too.
const inBranch = (branch, also) => (branch ? { branch, ...(also ? { also: 1 } : {}) } : {});

export const sendFileDirect = (person, file, body, thread = null, { mentions = [], branch = null, also = false } = {}) => upload(
  `${API_BASE}/chat/with/${person.kind}/${person.id}/files`, file, body,
  { ...inThread(branch ? null : thread), ...inBranch(branch, also) }, mentions);

export const sendFileGroup = (groupId, file, body, topic = null, { mentions = [], branch = null, also = false } = {}) => upload(
  `${API_BASE}/chat/groups/${groupId}/files`, file, body,
  { ...inTopic(branch ? null : topic), ...inBranch(branch, also) }, mentions);

/**
 * Onto the ticket, exactly as the ticket page's own Files section sends it.
 * A settled ticket and a ticket already holding twenty files both answer 409
 * with the reason in words; the caller shows those words rather than its own.
 */
export const uploadToTicket = (ticketId, file) => upload(
  `${API_BASE}/tickets/${ticketId}/attachments`, file);

/**
 * A chat file's bytes, as a Blob. FETCHED, not linked: every caller signs in
 * with a token in a header - a contractor's portal token, or staff's Entra
 * token through the interceptor - and a plain <a href> or <img src> carries
 * neither, so it would be answered 401. The same lesson the ticket's Files
 * list learned (components/TicketAttachments.js).
 */
export const chatFileBlob = (fileId) => axios
  .get(`${API_BASE}/chat/files/${fileId}/download`, { ...authFor(), responseType: 'blob' })
  .then((r) => r.data)
  .catch(async (err) => {
    // Asked for a blob, the server's {error} arrives as a blob too, and the
    // caller would show "Could not download" instead of the reason. Read back
    // into the shape every other call here fails with.
    const data = err.response?.data;
    if (data && typeof data.text === 'function') {
      try { err.response.data = JSON.parse(await data.text()); } catch { /* not JSON */ }
    }
    throw err;
  });

/** Fetch a chat file and hand it to the browser to save, under its own name. */
export const downloadChatFile = async (file) => {
  const blob = await chatFileBlob(file.id);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.file_name || 'file';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Let go on a delay: revoking at once races the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

/*
 * REACTIONS - a 👍 under a message instead of a line saying "thanks", as in
 * Slack, WhatsApp and Teams. One endpoint for every kind of conversation,
 * because a reaction is the same thing on a General line, a group line and a
 * ticket comment; `scope` says which table the message `id` (its uuid, not
 * its sequence number) is in.
 *
 * POST toggles mine and answers with that message's reactions as they now
 * stand, which is what the screen then shows - its own guess is only for the
 * moment in between. GET reads several messages' at once, for when the poll
 * says somebody else reacted. A 503 is a server that has not had the
 * reactions migration yet.
 */
export const react = ({ scope, id, emoji }) => axios
  .post(`${API_BASE}/chat/reactions`, { scope, id, emoji }, authFor())
  .then((r) => r.data);

export const reactionsFor = (scope, ids) => axios
  .get(`${API_BASE}/chat/reactions`, {
    ...authFor(), params: { scope, ids: ids.join(',') }
  }).then((r) => r.data);

/*
 * Both of these tell the rest of the page that the unread count has probably
 * moved - the bell asks again, and with it the Chat badge and the number on
 * the Jarvis icon (lib/appBadge) - so reading a conversation takes the count
 * down at once rather than at the next minute's poll.
 */
const announce = (name) => {
  try { window.dispatchEvent(new CustomEvent(name)); } catch { /* old browser */ }
};

export const markRead = (scope, id) => axios
  .post(`${API_BASE}/chat/read`, { scope, id }, authFor())
  .then((r) => { announce('jarvis:chat-read'); return r.data; });

/**
 * One poll of everything.
 *
 * Two cursors, because the two kinds of conversation are numbered differently:
 * a direct thread has a sequence, and a ticket thread is ticket_updates and
 * has only a timestamp. Group messages arrive as `groups` in the same batch
 * and need no third cursor: they are numbered from the SAME sequence as
 * direct messages, so `since` already covers both. The timeout is generous because the server holds the
 * request open for twenty seconds by design - without it, axios would abort
 * every poll and the screen would look as though it kept losing its
 * connection.
 */
export const events = ({
  since = '0', sinceAt, reactionsSince, changesSince, wait = true, signal
} = {}) => axios
  .get(`${API_BASE}/chat/events`, {
    ...authFor(),
    params: {
      since,
      ...(sinceAt ? { since_at: sinceAt } : {}),
      // A third cursor, for reactions: they change old messages, so neither
      // the sequence nor the ticket timestamp moves when one is added. Only
      // sent once the server has given one (null before its migration).
      ...(reactionsSince ? { reactions_since: reactionsSince } : {}),
      // And a fourth (084), for messages edited or deleted: like reactions,
      // they change old lines. Only once the server has given one.
      ...(changesSince ? { changes_since: changesSince } : {}),
      ...(wait ? {} : { wait: 0 }),
      // Whether somebody is actually at this keyboard, as opposed to a tab
      // left open. The server cannot tell the two apart and Away is computed
      // from it - see lib/chatStatus.js. It rides on the polls that already
      // exist rather than on a request of its own.
      ...STATUS.activeParam()
    },
    timeout: 40000,
    signal
  }).then((r) => r.data);

/**
 * Poll until stopped.
 *
 * A successful poll is reissued at once - that is what keeps the screen
 * current. A FAILED one backs off, because the usual reasons are a server
 * restarting mid-deploy and a laptop that has gone to sleep, and hammering
 * either is useless. Resets on the first success.
 *
 * Returns the stop function. Stopping is not optional: a loop left running
 * after the page has gone keeps a request in flight and keeps the person
 * looking present to everybody else.
 */
export function pollLoop(cursors, onBatch, onTrouble) {
  let stopped = false;
  let since = cursors.since || '0';
  let sinceAt = cursors.sinceAt || new Date().toISOString();
  let reactionsSince = cursors.reactionsSince || null;
  let changesSince = cursors.changesSince || null;
  let failures = 0;
  const controller = new AbortController();
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  (async () => {
    while (!stopped) {
      try {
        const batch = await events({ since, sinceAt, reactionsSince, changesSince, signal: controller.signal });
        if (stopped) return;
        failures = 0;
        if (onTrouble) onTrouble(null);
        since = batch.cursor || since;
        sinceAt = batch.cursor_at || sinceAt;
        reactionsSince = batch.reactions_cursor || reactionsSince;
        changesSince = batch.changes_cursor || changesSince;
        onBatch(batch);
        // New messages, somewhere: the counts on the icon and the bell move.
        const any = (k) => Array.isArray(batch?.[k]) && batch[k].length > 0;
        if (any('direct') || any('tickets') || any('groups') || any('replies') || any('changes')) {
          announce('jarvis:chat-activity');
        }
      } catch (error) {
        if (stopped || error.code === 'ERR_CANCELED') return;
        failures += 1;
        if (error.response?.status === 401) {
          if (onTrouble) onTrouble('Signed out. Reload to carry on.');
          return;
        }
        if (error.response?.status === 503) {
          // Chat has not been migrated on this server yet. Retrying every
          // second for ever would be rude as well as pointless.
          if (onTrouble) onTrouble(error.response?.data?.error || 'Chat is not available yet.');
          return;
        }
        if (onTrouble && failures > 1) onTrouble('Lost touch with the server — still trying.');
        await wait(Math.min(1000 * 2 ** (failures - 1), 15000));
      }
    }
  })();

  return () => { stopped = true; controller.abort(); };
}

/** The three groups, in the order Gordon asked for, with labels. */
export const GROUPS = [
  { key: 'active', label: 'Active' },
  { key: 'on_hold', label: 'On hold' },
  { key: 'finished', label: 'Finished' }
];

/*
 * BRANCHES (084) - replies to one message, under it, one level deep (Slack's
 * threads; "thread" already means a whole conversation here). `scope` is
 * 'direct' or 'group', `id` the message's uuid. Before 084 every one of
 * these answers 503.
 */

/**
 * The branches I follow, newest reply first: { branches, unread, next_offset,
 * answered_ready }. `filter`: 'unread', or (096) 'open' | 'answered'; nothing
 * is every branch.
 */
export const branchesList = ({ unread = false, filter = null, offset = 0 } = {}) => axios
  .get(`${API_BASE}/chat/branches`, { ...authFor(), params: {
    ...(filter ? { filter } : (unread ? { filter: 'unread' } : {})), ...(offset ? { offset } : {}) } })
  .then((r) => r.data);

/** Every branch I follow, read up to now. */
export const readAllBranches = () => axios
  .post(`${API_BASE}/chat/branches/read`, {}, authFor())
  .then((r) => { announce('jarvis:chat-read'); return r.data; });

/** One branch: { parent, replies, following, read_to, conversation, mention_people, may_mention_everyone }. */
export const branch = (scope, id) => axios
  .get(`${API_BASE}/chat/branches/${scope}/${id}`, authFor()).then((r) => r.data);

export const replyInBranch = (scope, id, body, { also = false, mentions = [] } = {}) => axios
  .post(`${API_BASE}/chat/branches/${scope}/${id}`,
    { body, ...(also ? { also_in_conversation: true } : {}), ...withMentions(mentions) }, authFor())
  .then((r) => r.data);

export const readBranch = (scope, id) => axios
  .post(`${API_BASE}/chat/branches/${scope}/${id}/read`, {}, authFor())
  .then((r) => { announce('jarvis:chat-read'); return r.data; });

export const followBranch = (scope, id, following) => axios
  .put(`${API_BASE}/chat/branches/${scope}/${id}/follow`, { following }, authFor())
  .then((r) => r.data);

/*
 * MARK ANSWERED AND MOVE TO A TOPIC (096). Both act on the branch as a whole.
 * Before 096 both answer 503, and the branch read says answered_ready: false.
 */

/** Mark the branch answered (true) or reopen it (false): { answered, reopened }. */
export const setBranchAnswered = (scope, id, answered) => axios
  .put(`${API_BASE}/chat/branches/${scope}/${id}/answered`, { answered }, authFor())
  .then((r) => r.data);

/** A group's branch becomes a topic: { topic: name } for a new one, { topic_id } for one the group has. */
export const moveBranch = (scope, id, { topic = null, topicId = null } = {}) => axios
  .post(`${API_BASE}/chat/branches/${scope}/${id}/move`, topicId ? { topic_id: topicId } : { topic }, authFor())
  .then((r) => r.data);

/*
 * EDIT AND DELETE (084): my own messages, any time; an administrator may
 * delete anybody's. The earlier wordings of an edited message are for the
 * people in the conversation to read; a deleted one's are nobody's.
 */
export const editMessage = (scope, id, body, mentions = []) => axios
  .patch(`${API_BASE}/chat/messages/${scope}/${id}`, { body, mentions }, authFor())
  .then((r) => r.data);

export const deleteMessage = (scope, id) => axios
  .delete(`${API_BASE}/chat/messages/${scope}/${id}`, authFor())
  .then((r) => r.data);

export const messageVersions = (scope, id) => axios
  .get(`${API_BASE}/chat/messages/${scope}/${id}/versions`, authFor())
  .then((r) => r.data);
